import { Box, Button, Container, Paper, TextField, Typography, Link } from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import SadAlert from '../../components/SadAlert';
import { apiClient } from '../../services/apiClient';
import AvanzuHeader from '../../components/AvanzuHeader';

const forgotPasswordSchema = yup.object({
  email: yup.string().email('Please enter a valid email address').required('Email is required'),
});

type ForgotPasswordFormData = yup.InferType<typeof forgotPasswordSchema>;

interface ForgotPasswordError {
  error: string;
  field?: 'email' | 'general';
}

const ForgotPassword: React.FC = () => {
  const [success, setSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState<ForgotPasswordError | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<ForgotPasswordFormData>({
    resolver: yupResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      setForgotPasswordError(null); // Clear any previous errors

      const response = await apiClient.post('/auth/forgot-password', data);

      if (!response.ok) {
        const errorData: ForgotPasswordError = await response.json();

        // Handle field-specific errors
        if (errorData.field === 'email') {
          setError('email', {
            type: 'server',
            message: errorData.error,
          });
        } else {
          // Handle general errors
          setForgotPasswordError(errorData);
        }
        return;
      }

      setSubmittedEmail(data.email);
      setSuccess(true);
    } catch (error) {
      console.error('Forgot password error:', error);
      setForgotPasswordError({
        error: 'Network error. Please check your connection and try again.',
        field: 'general',
      });
    }
  };

  if (success) {
    return (
      <Container maxWidth="xs">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          <Box sx={{ mb: 4 }}>
            <AvanzuHeader />
          </Box>
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Typography variant="h5" align="center" gutterBottom>
              Check your email
            </Typography>

            <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
              We've sent a password reset link to {submittedEmail}
            </Typography>

            <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 3 }}>
              <Link component={RouterLink} to="/login" underline="none">
                Back to Login
              </Link>
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xs">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Box sx={{ mb: 4 }}>
          <AvanzuHeader />
        </Box>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography variant="h4" align="center" gutterBottom>
            Forgot Password
          </Typography>

          <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
            Enter your email and we'll send you a reset link
          </Typography>

          {/* Display general error messages */}
          {forgotPasswordError && forgotPasswordError.field === 'general' && (
            <SadAlert sx={{ mb: 2 }}>{forgotPasswordError.error}</SadAlert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              {...register('email')}
              error={!!errors.email}
              helperText={errors.email?.message}
              disabled={isSubmitting}
              autoFocus
              margin="normal"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isSubmitting}
              sx={{ mt: 3, mb: 2 }}
            >
              {isSubmitting ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </Box>

          <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 3 }}>
            <Link component={RouterLink} to="/login" underline="none">
              Back to Login
            </Link>
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPassword;
